import { noAuthPost, post } from '@/api'
import jwtDecode from 'jwt-decode'

// Security threshold: refresh token when 15% of lifespan remains
const TOKEN_REFRESH_THRESHOLD = 0.15

class AuthenticationError extends Error {
  constructor(name, message, status = null) {
    super(message)
    this.name = name
    this.status = status
  }
}

function setToken(token) {
  try {
    localStorage.setItem('token', token)
  } catch (error) {
    throw new AuthenticationError('storage-error', 'Unable to store token')
  }
}

function getToken() {
  try {
    return localStorage.getItem('token')
  } catch (error) {
    return null
  }
}

function getNonEmptyToken() {
  const token = getToken()
  if (!token || token === '') {
    throw new AuthenticationError(
      'missing-token',
      'No token found in local storage',
    )
  }
  return token
}

function removeToken() {
  try {
    localStorage.removeItem('token')
  } catch (error) {
    // Silent fail for removeToken is acceptable
  }
}

function decodeToken(token) {
  try {
    return jwtDecode(token)
  } catch (error) {
    throw new AuthenticationError(
      'invalid-token',
      'The provided token is not decodable',
    )
  }
}

function getDecodedToken() {
  const token = getNonEmptyToken()
  return decodeToken(token)
}

async function authenticateUser(username, password) {
  if (!username || !password) {
    throw new AuthenticationError(
      'invalid-credentials',
      'Username and password are required',
    )
  }

  const authResponse = await noAuthPost('token-auth/', {
    data: { username, password },
  })

  if (authResponse.status === 200 && authResponse.data?.token) {
    const token = authResponse.data.token
    setToken(token)
    return token
  }

  return null
}

function isTokenSoonExpired(token) {
  if (!token) return true

  try {
    const tokenData = decodeToken(token)
    const now = Date.now() / 1000 // Convert to Unix timestamp

    // Check if token is already expired
    if (tokenData.exp <= now) return true

    // Calculate remaining lifespan percentage
    const tokenLifespan = tokenData.exp - tokenData.iat
    const remainingTime = tokenData.exp - now
    const remainingPercentage = remainingTime / tokenLifespan

    return remainingPercentage < TOKEN_REFRESH_THRESHOLD
  } catch (error) {
    // If we can't decode the token, consider it expired
    return true
  }
}

async function verifyToken() {
  const token = getToken()
  if (!token) {
    throw new AuthenticationError('missing-token', 'No token to verify')
  }

  try {
    return await post('token-verify/', { data: { token } })
  } catch (error) {
    throw new AuthenticationError(
      'verification-failed',
      'Token verification failed',
      error.response?.status,
    )
  }
}

async function refreshToken(token) {
  if (!token) {
    throw new AuthenticationError(
      'missing-token',
      'No token provided for refresh',
    )
  }

  try {
    const response = await post('token-refresh/', { data: { token } })

    if (response.data?.token) {
      const newToken = response.data.token
      setToken(newToken)
      return newToken
    }

    throw new AuthenticationError(
      'refresh-failed',
      'No token received from refresh endpoint',
    )
  } catch (error) {
    throw new AuthenticationError(
      'refresh-failed',
      'Error occurred when trying to refresh the token',
      error.response?.status,
    )
  }
}

async function getUserToken(username) {
  if (!username) {
    throw new AuthenticationError(
      'invalid-username',
      'Username is required for impersonation',
    )
  }

  try {
    const payload = { user: username }
    const response = await post('token-auth/impersonate/', { data: payload })

    if (response.status === 201 && response.data?.token) {
      return response.data.token
    }

    return null
  } catch (error) {
    throw new AuthenticationError(
      'impersonation-failed',
      `Failed to get token for user: ${username}`,
      error.response?.status,
    )
  }
}

async function authenticateAsUser(username) {
  const token = await getUserToken(username)
  if (token) {
    setToken(token)
  }
  return token
}

export {
  authenticateUser,
  authenticateAsUser,
  getToken,
  setToken,
  getDecodedToken,
  verifyToken,
  isTokenSoonExpired,
  refreshToken,
  removeToken,
}
